import {OnInit, AfterViewInit, OnChanges, Component, ElementRef, EventEmitter, NgZone, OnDestroy, Input, Output, ViewChild, SimpleChanges} from '@angular/core';
import {GoogleMap, MapInfoWindow, MapMarker, MapGeocoder} from '@angular/google-maps';
import {Observable, Subscription} from 'rxjs';
import {GoogleMapsService} from '../../../services/google/google-maps.service';
import {GeocodingLocation} from '../geocoding/geocodingLocation';
import {GeocoderResponse} from '../geocoding/geocoderResponse';
import {SnackService} from '../../../services/snack/snack.service';
import {TranslateService} from '@ngx-translate/core';
import {AuthService} from '../../../services/auth/auth.service';
import {UtilService} from '../../../services/util/util.service';

export interface FieldSuffixButtonConf {
  icon?: string;
  label?: string;
}

@Component({
  selector: 'app-google-autocomplete',
  templateUrl: './google-autocomplete.component.html',
  styleUrls: ['./google-autocomplete.component.scss']
})
export class GoogleAutocompleteComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

  apiLoaded: Observable<boolean>;
  mapLoading: boolean;

  @ViewChild(GoogleMap, { static: false }) map: GoogleMap;
  @ViewChild(MapInfoWindow, { static: false }) infoWindow: MapInfoWindow;
  @ViewChild('search') public searchElement: ElementRef;

  private subscriptions: Subscription[] = [];

  options = {
    fields: ['formatted_address', 'name', 'website', 'address_components', 'geometry']
  };

  // Default coordinates
  readonly coordinates: Record<string, google.maps.LatLngLiteral> = {
    'Europe/Tallinn': {lat: 59.436962, lng: 24.753574},
    'Europe/Riga': {lat: 56.946285, lng: 24.105078},
    'Europe/Vilnius': {lat: 54.687157, lng: 25.279652},
    'Europe/Minsk': {lat: 53.893009, lng: 27.567444}
  };

  mapZoom = 12;
  mapCenter: google.maps.LatLng;
  mapOptions: google.maps.MapOptions;
  placesService: google.maps.places.PlacesService;

  markerInfoContent = '';
  markerOptions: google.maps.MarkerOptions;

  geocoderWorking = false;
  geolocationWorking = false;
  isMarkerVisible = false;

  @Input() public suffixButton: FieldSuffixButtonConf;
  @Output() public suffixButtonClick: EventEmitter<any> = new EventEmitter<any>();

  @Input() cssClass: string;
  @Input() readOnly: boolean;
  @Input() address: string;
  @Input() hideMap: boolean;
  @Input() hideSearch: boolean;
  @Input() addressCleared: string;
  @Output() addressSelected = new EventEmitter<GeocodingLocation>();

  constructor(private ngZone: NgZone,
              private googleMapsService: GoogleMapsService,
              private snackService: SnackService,
              private translateService: TranslateService,
              private geocoder: MapGeocoder,
              public authService: AuthService,
              public utilService: UtilService) {
    this.mapLoading = true;
    this.apiLoaded = this.googleMapsService.isApiLoaded$;
  }

  get isWorking(): boolean {
    return this.geolocationWorking || this.geocoderWorking;
  }

  openInfoWindow(marker: MapMarker) {
    this.infoWindow.open(marker);
  }

  ngAfterViewInit(): void {
    // Make sure that google API and search elements are loaded before proceeding.
    this.subscriptions.push(this.apiLoaded.subscribe((res: boolean) => {
      if (res && !this.readOnly && !this.hideSearch) {
        this.getAutocomplete();
      }
    }));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.address && !this.mapLoading) {
      this.panToAddress(changes.address.currentValue);
    }
    if (changes.addressCleared && changes.addressCleared.currentValue && !this.mapLoading) {
      this.mapZoom = 12;
      this.isMarkerVisible = false;
      this.setDefaults();
      this.panToAddress(this.address);
    }
  }

  ngOnInit(): void {
    // Make sure that google API and search elements are loaded before proceeding.
    this.subscriptions.push(this.apiLoaded.subscribe((res: boolean) => {
      if (res) {
        this.mapOptions = {
          mapTypeId: google.maps.MapTypeId.ROADMAP,
          zoomControl: true,
          disableDoubleClickZoom: true,
          maxZoom: 20,
          minZoom: 4,
          streetViewControl: false,
          mapTypeControl: false
        };
        this.markerOptions = {
          draggable: !this.readOnly,
          animation: google.maps.Animation.DROP,
        };
        this.setDefaults();
        this.panToAddress(this.address);
        this.mapLoading = false;
      }
    }));
  }

  setDefaults(): void {
    this.mapCenter = new google.maps.LatLng(this.coordinates[this.authService.user.timezone]);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  panToAddress(address: string): void {
    if (address && address != 'undefined') {
      const request: google.maps.GeocoderRequest = { address: address };
      this.geocoder.geocode(request).subscribe((res) => {
        if (res.status === 'OK' && res.results?.length) {
          const place = res.results[0];
          if (place.geometry?.location) {
            this.mapCenter = place.geometry.location;
            setTimeout(() => {
              if (this.map !== undefined) {
                this.map.panTo(place.geometry.location);
              }
            }, 500);
            this.markerInfoContent = place.formatted_address;
            if (place.partial_match && place.types.includes('country')) {
              this.mapZoom = 6;
            } else {
              this.isMarkerVisible = true;
              this.mapZoom = 12;
            }
          }
        }
      });
    } else {
      setTimeout(() => {
        if (this.map !== undefined) {
          this.map.panTo(this.mapCenter);
        }
      }, 500);
    }
  }

  getAutocomplete(): void {
    // Binding autocomplete to search input control
    const autocomplete = new google.maps.places.Autocomplete(
      this.searchElement.nativeElement, this.options
    );

    autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        const place = autocomplete.getPlace();
        if (!place.formatted_address) {
          // User entered the name of a Place that was not suggested.
          return;
        }
        this.geocode({location: place.geometry.location});
        this.searchElement.nativeElement.value = '';
      });
    });
  }

  updateAddress(place: google.maps.places.PlaceResult, placeId: string) {
    let geocodingLocation: GeocodingLocation = new GeocodingLocation();
    geocodingLocation.placeId = placeId;
    if (place.name) geocodingLocation.name = place.name;
    if (place.website) geocodingLocation.website = place.website;
    geocodingLocation.latitude = place?.geometry?.location?.lat();
    geocodingLocation.longitude = place?.geometry?.location?.lng();

    let streetNumber = '';
    let streetName = '';
    // Fallbacks for street
    let premise = '';
    let pointOfInterest = '';
    // Fallback for city
    let sublocality = '';
    let neighborhood = '';
    place.address_components.forEach(component => {
      if (component.types.includes('country')) {
        geocodingLocation.countryCode = component.short_name;
      } else if (component.types.includes('locality')) {
        geocodingLocation.city = component.short_name;
      } else if (component.types.includes('sublocality') && component.types.includes('sublocality_level_1')) {
        sublocality = component.short_name;
      } else if (component.types.includes('neighborhood')) {
        neighborhood = component.short_name;
      } else if (component.types.includes('administrative_area_level_1')) {
        geocodingLocation.county = component.short_name;
      } else if (component.types.includes('street_number')) {
        streetNumber = component.short_name;
      } else if (component.types.includes('route')) {
        streetName = component.short_name;
      } else if (component.types.includes('premise')) {
        premise = component.short_name;
      } else if (component.types.includes('point_of_interest')) {
        pointOfInterest = component.short_name;
      }
    });
    if (!geocodingLocation.city?.length) {
      geocodingLocation.city = sublocality;
      if (neighborhood) {
        geocodingLocation.city = geocodingLocation.city + '-' + neighborhood;
      }
    } else if (!geocodingLocation.county?.length) {
      geocodingLocation.county = sublocality;
    }
    if (streetName && premise && streetNumber) {
      streetNumber = premise + "/" + streetNumber;
    }
    if (!streetName?.length) {
      const formattedAddressStreet: string = place.formatted_address?.split(',')[0];
      streetName = premise || pointOfInterest || formattedAddressStreet;
    }
    geocodingLocation.location = streetName;
    if (streetNumber?.length) {
      geocodingLocation.location = geocodingLocation.location + " " + streetNumber;
    }
    this.addressSelected.emit(geocodingLocation);
  }

  geocodeLatLng(location: google.maps.LatLngLiteral): Promise<GeocoderResponse> {
    let geocoder = new google.maps.Geocoder();

    return new Promise((resolve, reject) => {
      geocoder.geocode({ 'location': location }, (results, status) => {
        const response = new GeocoderResponse(status, results);
        resolve(response);
      });
    });
  }

  getCurrentLocation() {
    if (!navigator.geolocation) {
      this.snackService.showError(this.translateService.instant('general.api_venues_google_maps_geolocation_disabled'));
      return;
    }
    this.geolocationWorking = true
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.geolocationWorking = false;

        const point: google.maps.LatLngLiteral = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        this.geocoderWorking = true;
        this.geocodeLatLng(point)
          .then((response: GeocoderResponse) => {
            if (response.status === 'OK' && response.results?.length) {
              const place = response.results[0];

              this.mapCenter = new google.maps.LatLng(point);
              this.mapZoom = 12;
              this.map.panTo(point);
              this.markerInfoContent = place.formatted_address;
              this.isMarkerVisible = true;

              this.requestPlaceDetails(place);
            } else {
              this.snackService.showError(response.error_message);
            }
          })
          .finally(() => {
            this.geocoderWorking = false;
          });
      },
      (error) => {
        this.geolocationWorking = false;

        if (error.PERMISSION_DENIED) {
          this.snackService.showError(this.translateService.instant('general.api_venues_google_maps_permission_denied'));
        } else if (error.POSITION_UNAVAILABLE) {
          this.snackService.showError(this.translateService.instant('general.api_venues_google_maps_position_unavailable'));
        } else if (error.TIMEOUT) {
          this.snackService.showError(this.translateService.instant('general.api_venues_google_maps_timed_out'));
        } else {
          this.snackService.showBackendError(error);
        }
      },
      { enableHighAccuracy: true }
    );
  }

  onMapDragEnd(event: google.maps.MapMouseEvent) {
    const point: google.maps.LatLngLiteral = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    };

    this.geocoderWorking = true;
    this.geocodeLatLng(point)
      .then((response: GeocoderResponse) => {
        if (response.status === 'OK') {
          if (response.results.length) {
            const place = response.results[0];

            this.mapCenter = new google.maps.LatLng(point);
            this.mapZoom = 12;
            this.map.panTo(point);
            this.markerInfoContent = place.formatted_address;
            this.isMarkerVisible = true;

            this.requestPlaceDetails(place);
          }
        }
      })
      .finally(() => {
        this.geocoderWorking = false;
      });
  }

  click(event: google.maps.MapMouseEvent) {
    this.geocode({location: event.latLng});
  }

  geocode(request: google.maps.GeocoderRequest): void {
    this.geocoder.geocode(request)
      .subscribe((res) => {
        if (res.status === 'OK' && res.results?.length) {
          const place = res.results[0];
          this.mapCenter = place.geometry.location;
          this.mapZoom = 12;
          this.markerInfoContent = place.formatted_address;
          this.isMarkerVisible = true;

          setTimeout(() => {
            if (this.map !== undefined) {
              this.map.panTo(place.geometry.location);
            }
          }, 500);

          this.requestPlaceDetails(place);
        }
      });
  }

  requestPlaceDetails(placeResult: google.maps.places.PlaceResult) {
    if (this.placesService == null) {
      this.placesService = new google.maps.places.PlacesService(this.map.googleMap);
    }

    const placeRequest = {
      placeId: placeResult.place_id,
      fields: this.options.fields
    };

    this.placesService.getDetails(placeRequest, (results, status) => {
      if (status === google.maps.places.PlacesServiceStatus.OK) {
        this.updateAddress(results, placeRequest.placeId);
      }
    });
  }
}
